// Pure car constants shared by the driving scenes. No Phaser, no config imports.
// Car specs themselves (price, consumption, accel…) come from the server catalog
// at runtime — only layout/physics constants live here.

// Mirrors backend app/game/catalog.py.
export const PX_PER_KMH = 14;          // world px per second, per km/h
export const PX_PER_M = 50.4;          // world px per metre (14 px/s at 1 km/h)
export const MAX_VISUAL_PX_S = 2000;   // visual speed cap so fast cars stay readable
export const MISFUEL_DIE_AFTER_M = 100; // a misfuelled car dies after this distance

// Street lanes, as the line the WHEELS stand on: the car drives on `left` when heading
// left, `right` when heading right. World y, measured off bg_street.webp — the asphalt runs
// from y 528 (the kerb below the pavement) to y 643 (the grass verge), so the near lane sits
// just inside its lower edge. The far lane is 70 px up the road: the practical-lesson
// segments (bg_seg_*, normalised to one layout) paint their centre line at world y 572, and
// at the old 592 the far-lane car's tyres sat right on it — 562 keeps them 10 px above the
// line, and on the street it is simply the middle of the far lane. A model's sprite centre
// is `ROAD_WHEEL_Y[lane] - carWheelOffset(model)` (see carLaneY): the art's transparent
// padding differs per model, so centring them all on one y left every car at its own wheel
// height.
export const ROAD_WHEEL_Y = { left: 562, right: 632 };
// The lay-by along a `parking` segment (bg_seg_parking's bottom band, world y 652-720): a
// third position you pull down into to park — only offered while the car is beside it.
export const BAY_WHEEL_Y = 704;
/** The wheel line for any lane id, the lay-by included. */
export const wheelYForLane = (lane) => (lane === 'bay' ? BAY_WHEEL_Y : ROAD_WHEEL_Y[lane === 'left' ? 'left' : 'right']);

export const UTURN_MS = 600;   // turn-around animation length
export const REVERSE_KMH = 10; // reverse is a fixed crawl

// The town's own speed limit, shown on the HUD's sign for the whole street. Routes set
// their own per segment (see the `limit` tasks in routeRules.mjs).
export const STREET_LIMIT_KMH = 50;

// v2 practical-lesson routes (see scenes/lessonScript.mjs, scenes/segmentFeatures.mjs):
// routes are assembled from reusable road segments instead of one fixed scripted route.
export const LANE_CHANGE_MS = 600;      // lane-change animation length
export const SEGMENT_WORLD_WIDTH = 2557; // width of one road segment, in world px
export const ROUTE_START_OFFSET = 300;  // the car starts this far into the first segment
export const ROUTE_FINISH_X = 200;      // the route's finish line, in world px from its end

// On-screen car heights (px) for the two sprite orientations. Kept as the anchor the
// front portrait is scaled against (see FRONT_PX_PER_M) and as a fallback size for code
// that has no sprite to measure — everything that DRAWS a car goes through
// `carDisplayHeight(model, view)` instead, which sizes each model by its real height.
export const CAR_DISPLAY = { side: { h: 150 }, front: { h: 200 } };

// Where the licence plate sits inside each model's front portrait, in the SOURCE pixels of
// that model's own art (400 px tall; 600 px wide, 667 for c8/yaris_l). CarSprite scales the
// box by the same factor it scales the body, so these numbers travel with the art.
// Traced 2026-08-28 from the webp files: the plate's white (green for the Tesla's EV plate)
// rectangle was located by thresholding, then read off a 4x zoom with a 20 px grid.
// `angle` is 0 unless the plate is visibly tilted in the ¾ view — only the B-class is.
//
// PLATE_BOX is the FLAT fallback (Canvas renderer, or a model with no quad below). Under
// WebGL the text is mapped onto PLATE_QUAD instead, so it takes on the plate's real tilt.
export const PLATE_BOX = {
  panda:   { x: 129, y: 276, w: 76, angle: 0 },
  bclass:  { x: 103, y: 278, w: 70, angle: 4 },
  q5:      { x: 103, y: 255, w: 71, angle: 0 },
  t03:     { x: 124, y: 264, w: 71, angle: 0 },
  model3:  { x: 88,  y: 262, w: 52, angle: 0 },
  c8:      { x: 126, y: 264, w: 63, angle: 0 },
  yaris_l: { x: 121, y: 289, w: 60, angle: 0 },
};

// The plate is not a flat rectangle in any of these portraits: each car is drawn very
// slightly from one side, so the white plate is a QUAD — the panda's tips 7 px down to the
// right, the C8's tapers. Traced 2026-08-30 by flood-filling the plate colour out from
// PLATE_BOX's centre in each front webp and reading the extreme columns' top/bottom rows
// (columns whose run height is an outlier are dropped, so the anti-aliased fringe and the
// C8's white bumper spill are ignored). Same SOURCE pixels as PLATE_BOX; CarSprite scales
// them by the factor it scaled the body by, and maps the plate text onto the quad with a
// two-triangle Mesh, so the lettering tilts and tapers with the plate it sits on.
export const PLATE_QUAD = {
  panda:   { tl: [95, 263],  tr: [161, 270], br: [161, 291], bl: [95, 284] },
  bclass:  { tl: [70, 268],  tr: [135, 275], br: [135, 294], bl: [70, 286] },
  q5:      { tl: [68, 242],  tr: [137, 246], br: [137, 265], bl: [68, 260] },
  t03:     { tl: [89, 250],  tr: [158, 254], br: [158, 278], bl: [89, 272] },
  model3:  { tl: [63, 249],  tr: [112, 253], br: [112, 275], bl: [63, 270] },
  c8:      { tl: [96, 251],  tr: [149, 255], br: [149, 281], bl: [96, 277] },
  yaris_l: { tl: [91, 276],  tr: [145, 281], br: [145, 305], bl: [91, 300] },
};

// Petrol station: pump x positions in station-scene coordinates.
export const PUMPS = [{ x: 451 }, { x: 807 }];
export const PUMP_RANGE = 90;        // how close the car must stop to use a pump
export const NEAR = 160;             // generic "close enough to interact" radius
export const HOUSE_PARK_RANGE = 150; // parking near your own house

// --- one real-world scale outdoors ------------------------------------------------------
//
// On the two outdoor driving scenes (the street and the training route) every movable thing
// is drawn at the SAME metres→pixels ratio, so a 1.2 m child stands next to a 1.54 m Fiat
// Panda and looks like a child standing next to a small car. Indoors nothing changes: the
// character keeps her 240 px art size and the shops/house keep their layouts.
export const OUTDOOR_PX_PER_M = 100;
export const CHARACTER_HEIGHT_M = 1.2;
// NatalkaSprite draws the character 240 px tall (NATALKA_H); kept here so this module stays
// Phaser-free. A scene that wants real-world scale sets `scene.worldScale` to this.
export const CHARACTER_ART_H = 240;
export const OUTDOOR_CHARACTER_SCALE = CHARACTER_HEIGHT_M * OUTDOOR_PX_PER_M / CHARACTER_ART_H;
// Half of that: the distance from a character's sprite centre to her feet, outdoors, on the
// reference plane. The `baseOffset` every person-shaped thing hands to the two perspective
// converters above (streetSpawns, RemoteAvatar, NpcAvatar, the route's pedestrian).
export const CHARACTER_HALF_H = CHARACTER_ART_H * OUTDOOR_CHARACTER_SCALE / 2;   // 60

// Real cars, roof height and length in metres (the length is documentation — only the
// height drives the drawing, the art keeps its own aspect ratio).
export const CAR_DIMENSIONS_M = {
  panda:   { h: 1.54, l: 3.54 },
  bclass:  { h: 1.60, l: 4.27 },
  q5:      { h: 1.66, l: 4.63 },
  t03:     { h: 1.58, l: 3.62 },
  model3:  { h: 1.44, l: 4.69 },
  c8:      { h: 1.23, l: 4.63 },
  yaris_l: { h: 1.50, l: 3.94 },
};

// How much of each image is actually car: `solid body / image`, measured 2026-09-03 with
// Pillow (alpha >= 128 — the 2026-09-03 art with see-through windows has a soft drop shadow
// under the body that the plain alpha bbox would count as car) over the webp files in
// public/assets/items. This is why the cars looked far too small before the first
// measurement — a Panda drawn 150 px "tall" was mostly transparent padding, and the padding
// differs wildly between models (the C8 is only 42% car).
export const CAR_CONTENT_FRAC = {
  side: {   // art 450×300 (501×300 for c8 / yaris_l): body height / 300
    panda: 0.783, bclass: 0.717, q5: 0.603, t03: 0.740,
    model3: 0.473, c8: 0.420, yaris_l: 0.703,
  },
  sideWidth: {   // body length / image width (450 or 501)
    panda: 0.947, bclass: 0.980, q5: 0.984, t03: 0.978,
    model3: 0.969, c8: 0.936, yaris_l: 0.876,
  },
  front: {  // art 600×400 (667×400 for c8 / yaris_l)
    panda: 0.882, bclass: 0.858, q5: 0.772, t03: 0.865,
    model3: 0.627, c8: 0.603, yaris_l: 0.870,
  },
  // Where the body ENDS inside the side art: `solid bbox bottom / image height`. The padding
  // is not symmetric (the Panda's body nearly touches the top edge, the Corvette floats 91 px
  // down a 300 px canvas), so this — not the height fraction — is what puts every model's
  // wheels on one line.
  sideBottom: {
    panda: 0.867, bclass: 0.830, q5: 0.800, t03: 0.860,
    model3: 0.743, c8: 0.723, yaris_l: 0.837,
  },
};

// The windows people are seen through in each side sprite, in that art's SOURCE pixels
// (450/501 × 300; the art faces LEFT, so `driver` is the leftmost enclosed transparent hole
// and `rear` the next one — measured 2026-09-03 by flood-filling the alpha). The Corvette
// has one long window: its passenger sits further back in the same one.
export const CAR_SEATS = {
  panda:   { driver: { x0: 165, x1: 250, y0: 69, y1: 124 }, rear: { x0: 270, x1: 334, y0: 68, y1: 120 } },
  bclass:  { driver: { x0: 155, x1: 232, y0: 74, y1: 121 }, rear: { x0: 251, x1: 321, y0: 73, y1: 114 } },
  q5:      { driver: { x0: 174, x1: 244, y0: 77, y1: 114 }, rear: { x0: 261, x1: 317, y0: 77, y1: 110 } },
  t03:     { driver: { x0: 171, x1: 252, y0: 62, y1: 109 }, rear: { x0: 273, x1: 338, y0: 62, y1: 103 } },
  model3:  { driver: { x0: 174, x1: 245, y0: 96, y1: 127 }, rear: { x0: 265, x1: 314, y0: 95, y1: 122 } },
  c8:      { driver: { x0: 209, x1: 278, y0: 104, y1: 128 }, rear: { x0: 254, x1: 323, y0: 104, y1: 128 } },
  yaris_l: { driver: { x0: 200, x1: 280, y0: 100, y1: 138 }, rear: { x0: 302, x1: 350, y0: 100, y1: 132 } },
};

// The same measurement for the FRONT portraits, over the 400 px-tall webp files
// (2026-08-30). NOT the plain alpha bbox this time: several of these portraits have a soft
// drop shadow painted under the car, and the shadow is what the alpha box ends at — the
// Panda's runs 34 px past its tyres. The number that matters is where the SOLID car ends, so
// this is `magick <file> -alpha extract -threshold 50% -format %@ info:`.
// The art floats by wildly different amounts inside its canvas (the Corvette's tyres stop
// 75 px short of the bottom edge, the Yaris's 26), which is why every showroom car used to
// hover its own distance above the podium back when they all shared one sprite-centre y.
export const CAR_FRONT_CONTENT_BOTTOM = {
  panda: 0.915, bclass: 0.915, q5: 0.877, t03: 0.917,
  model3: 0.823, c8: 0.812, yaris_l: 0.935,
};

// The front portrait is showroom/garage furniture, not street traffic: it keeps the layout
// it has always had, so the scale is pinned to "the tallest car still fills 200 px of body"
// (the Q5, exactly what CAR_DISPLAY.front.h used to draw for every model). The other six
// then come out correctly SHORTER than it instead of all being the same height.
export const FRONT_PX_PER_M = CAR_DISPLAY.front.h / CAR_DIMENSIONS_M.q5.h;   // ≈ 120.48

const FALLBACK_MODEL = 'panda';

/**
 * How tall to DRAW a car's image (px), padding included, so that the visible body comes
 * out at its real height: `OUTDOOR_PX_PER_M` px per metre in the side view (the street),
 * `FRONT_PX_PER_M` in the front portrait. An unknown model is drawn as a Panda.
 */
// Side art aspect (image width / height): 450/300 for most, 501/300 for c8 and yaris_l.
export const SIDE_ART_ASPECT = { c8: 501 / 300, yaris_l: 501 / 300 };
const sideAspect = (key) => SIDE_ART_ASPECT[key] ?? 450 / 300;

export function carDisplayHeight(model, view = 'side') {
  const v = view === 'front' ? 'front' : 'side';
  const key = CAR_DIMENSIONS_M[model] ? model : FALLBACK_MODEL;
  const pxPerM = v === 'front' ? FRONT_PX_PER_M : OUTDOOR_PX_PER_M;
  const { h, l } = CAR_DIMENSIONS_M[key];
  const byHeight = h * pxPerM / CAR_CONTENT_FRAC[v][key];
  if (v === 'front') return byHeight;
  // Side view: the art's proportions are not the real car's (the Panda is drawn squat, the
  // Tesla long and low), so fitting the HEIGHT alone made a Tesla twice a Panda's length on
  // the road. Fit the LENGTH too and take the geometric mean of the two: the drawn body's
  // height × length then equals the real car's, and no model comes out twice another.
  const byLength = (l * pxPerM / CAR_CONTENT_FRAC.sideWidth[key]) / sideAspect(key);
  return Math.sqrt(byHeight * byLength) * CAR_SIDE_SCALE;
}

// Every car on the street is drawn this much smaller than true scale — the owner's call
// (2026-09-03): at 100 px/m the cars crowded the two-lane road and the child beside them.
export const CAR_SIDE_SCALE = 0.9;

/**
 * How far below a FRONT portrait's centre its tyres are, at the size it is drawn (px).
 * `sprite.y = podiumY - carFrontWheelOffset(model)` stands every showroom model on one line.
 * The front view is indoor furniture with no perspective, so there is no ground-line argument.
 */
export function carFrontWheelOffset(model) {
  const key = CAR_FRONT_CONTENT_BOTTOM[model] ? model : FALLBACK_MODEL;
  return carDisplayHeight(key, 'front') * (CAR_FRONT_CONTENT_BOTTOM[key] - 0.5);
}

// --- y-perspective ----------------------------------------------------------------------
//
// The street is drawn from slightly above and behind the pavement, so the road runs AWAY
// from the viewer going up the screen: the near kerb is closer to the camera than the
// shopfronts are. Everything that stands on the ground is therefore scaled by how far DOWN
// the screen its ground line is — the pavement's walking line is the reference plane (a
// 1.2 m child is 120 px there, exactly as before) and the near edge of the asphalt is
// `nearScale` times bigger. Linear in y, which for the 150 px of ground the street shows is
// indistinguishable from the real 1/z and has the enormous advantage of being invertible.
//
//   refY      the pavement's feet line (STREET_FEET_Y) — scale 1, nothing changes size there
//   nearY     the grass verge at the bottom of the asphalt (STREET_ASPHALT_BOTTOM_Y)
//   nearScale how much bigger a thing standing on `nearY` is drawn
//
// `groundY` is always the FEET/WHEEL line, never the sprite centre: sprites scale about
// their centre, so the two differ by an amount that itself depends on the scale (see
// groundYFromCentre for the inverse).
export const PERSPECTIVE = { refY: 518, nearY: 643, nearScale: 1.25 };

// The Podwórko is a much deeper view than the street: its lawn runs from the fence line
// (y ≈ 380, where a 1 m fence is ~54 px) down to the bottom of the frame, so a child at
// the fence is drawn smaller than on the reference plane (farScale < 1) and grows more on
// the way down. A scene picks its profile with `scene.perspective` (default: the street's).
export const BACKYARD_PERSPECTIVE = { refY: 380, nearY: 700, nearScale: 1.3, farScale: 0.7 };

/** How much bigger something standing on `groundY` is drawn: `farScale` (default 1) at or
 * above `refY`, `nearScale` at or below `nearY`, linear in between. */
export function perspectiveScale(groundY, profile = PERSPECTIVE) {
  const { refY, nearY, nearScale, farScale = 1 } = profile;
  if (!Number.isFinite(groundY)) return farScale;
  const t = (groundY - refY) / (nearY - refY);
  return farScale + (nearScale - farScale) * (t < 0 ? 0 : t > 1 ? 1 : t);
}

/**
 * The sprite-CENTRE y of something whose ground line is `groundY` and whose distance from
 * centre to ground is `baseOffset` at scale 1 (the offset grows with the sprite).
 */
export function centreYFromGround(baseOffset, groundY, profile = PERSPECTIVE) {
  return groundY - baseOffset * perspectiveScale(groundY, profile);
}

/**
 * The inverse: given a sprite CENTRE y, where does it touch the ground? Needed by everything
 * that only ever sees a centre — a peer's broadcast position, a bike parked at a remembered
 * y, a walker whose controller moves the centre. Solves `g = c + baseOffset·s(g)` exactly;
 * `s` is piecewise linear, so this is one division plus two clamp checks.
 */
export function groundYFromCentre(baseOffset, centreY, profile = PERSPECTIVE) {
  const { refY, nearY, nearScale, farScale = 1 } = profile;
  const flat = centreY + baseOffset * farScale;       // if the answer is above refY, s = farScale
  if (flat <= refY) return flat;
  const near = centreY + baseOffset * nearScale;      // …and below nearY, s = nearScale
  if (near >= nearY) return near;
  const k = (nearScale - farScale) / (nearY - refY);
  return (centreY + baseOffset * (farScale - k * refY)) / (1 - baseOffset * k);
}

/**
 * How far below a side-view car's CENTRE its wheels are, at the size it is drawn on the
 * ground line `wheelY` (px). `sprite.y = wheelY - carWheelOffset(model, wheelY)` puts a Panda
 * and a Q5 on the same tarmac; an unknown model is treated as a Panda. Omitting `wheelY`
 * asks for the un-foreshortened offset (the reference plane) — that is the number to use as
 * `baseOffset` in groundYFromCentre.
 */
export function carWheelOffset(model, wheelY = PERSPECTIVE.refY) {
  const key = CAR_DIMENSIONS_M[model] ? model : FALLBACK_MODEL;
  return carDisplayHeightAt(key, wheelY) * (CAR_CONTENT_FRAC.sideBottom[key] - 0.5);
}

/** Side-view display height (px) of a car whose wheels are on `wheelY`. */
export function carDisplayHeightAt(model, wheelY = PERSPECTIVE.refY) {
  return carDisplayHeight(model, 'side') * perspectiveScale(wheelY);
}

/** Where a side-view car whose sprite centre is at `centreY` puts its wheels. */
export function carGroundYFromCentre(model, centreY) {
  return groundYFromCentre(carWheelOffset(model), centreY);
}

/** The sprite-centre y a car of this model gets when it drives/parks in `facing`'s lane. */
export function carLaneY(model, facing = 'right') {
  const wheelY = ROAD_WHEEL_Y[facing === 'left' ? 'left' : 'right'];
  return centreYFromGround(carWheelOffset(model), wheelY);
}

// Bikes: a child on a bike is about 1.4 m to the top of her head — the layered rider's box (bike_rig.json's
// riderHeightPx) is drawn that tall outdoors; a parked bike keeps the art's own ratio to it (parkedHeightPx).
export const BIKE_HEIGHT_M = { rider: 1.4 };

/**
 * Display height (px) of a bike sprite: the layered BikeSprite's box, head (or handlebars) to ground. With `rig`
 * (bike_rig.json's riderHeightPx and parkedHeightPx) the rider is `riderH` tall and the parked bike keeps the art's
 * own ratio, so both are drawn at one scale. Without it (the rig failed to load) both are `riderH`.
 */
export function bikeDisplayHeight(kind, type = 'pink', rig = null, riderH = BIKE_HEIGHT_M.rider * OUTDOOR_PX_PER_M) {
  if (kind !== 'parked' || !rig) return riderH;
  const parkedPx = rig.parkedHeightPx[type] ?? rig.parkedHeightPx.pink;
  return riderH * parkedPx / rig.riderHeightPx;
}

/** Px from a bike sprite's centre to its wheels, the car function's twin: the box ends on the ground line, so half
 *  its height. */
export function bikeWheelOffset(kind, type = 'pink', rig = null, riderH) {
  return bikeDisplayHeight(kind, type, rig, riderH) / 2;
}

/**
 * Getting on a parked bike whose wheels stand on `groundY`: the rider's centre, so the ridden bike's wheels are
 * where the parked bike's were (G11, owner 2026-10-24: "when I get on the bike I should be where the bike was").
 * Getting off stores that centre, so on and off again and again never moves the bike.
 */
export function riderOnParkedBike(type, groundY, profile = PERSPECTIVE, rig = null) {
  return centreYFromGround(bikeWheelOffset('rider', type, rig), groundY, profile);
}

/**
 * A parked bike drawn read-only on an outdoor scene (a host's bike on a visit). The stored
 * y is the RIDER's centre at dismount (or a scene default given as a parked centre); both
 * go through the ground line so the shorter parked art stands where the wheels were. The sizes are the layered
 * BikeSprite's (`rig`: bike_rig.json), the same as the owner's BikeController.
 */
export function parkedBikeOutdoor(type, { storedY = null, defaultY }, profile = PERSPECTIVE, rig = null) {
  const stored = storedY != null;
  const groundY = groundYFromCentre(bikeWheelOffset(stored ? 'rider' : 'parked', type, rig), stored ? storedY : defaultY, profile);
  return {
    groundY,
    y: centreYFromGround(bikeWheelOffset('parked', type, rig), groundY, profile),
    h: bikeDisplayHeight('parked', type, rig) * perspectiveScale(groundY, profile),
  };
}
